/**
 * /belanja/transaksi — Riwayat Transaksi (plan 19 Fase 1, Task 1.6 Step 4).
 *
 * Gabungan payment_order + credit_ledger (GET /v1/billing/transactions),
 * terbaru dulu, infinite scroll pola HistoryList (FlatList + onEndReached).
 */

import { useCallback, useRef, useState } from 'react';
import { ActivityIndicator, FlatList, Pressable, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect, useRouter } from 'expo-router';

import { SaldoHistoryRow } from '@/components/belanja/SaldoHistoryRow';
import { ThemedText } from '@/components/themed-text';
import { ThemedView } from '@/components/themed-view';
import { Brand, Colors, MaxContentWidth, Spacing } from '@/constants/theme';
import { ApiError } from '@/lib/api/client';
import {
  getBillingTransactions,
  type BillingTransactionItem,
} from '@/lib/api/billing';

const PAGE_SIZE = 20;

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; items: BillingTransactionItem[]; hasMore: boolean }
  | { kind: 'error'; message: string };

export default function TransaksiScreen() {
  const router = useRouter();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRef = useRef(1);
  const loadingMoreRef = useRef(false);

  useFocusEffect(
    useCallback(() => {
      let cancelled = false;
      pageRef.current = 1;
      getBillingTransactions(1, PAGE_SIZE)
        .then((result) => {
          if (cancelled) return;
          setState({ kind: 'ready', items: result.items, hasMore: result.hasMore });
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setState({
            kind: 'error',
            message: err instanceof ApiError ? err.message : 'Gagal memuat transaksi. Coba lagi.',
          });
        });
      return () => {
        cancelled = true;
      };
    }, []),
  );

  const onEndReached = useCallback(() => {
    if (state.kind !== 'ready' || !state.hasMore) return;
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = pageRef.current + 1;
    getBillingTransactions(nextPage, PAGE_SIZE)
      .then((result) => {
        pageRef.current = nextPage;
        setState((prev) =>
          prev.kind === 'ready'
            ? { kind: 'ready', items: [...prev.items, ...result.items], hasMore: result.hasMore }
            : prev,
        );
      })
      .catch(() => {
        // Gagal load page berikutnya — biarkan; user bisa scroll lagi.
      })
      .finally(() => {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      });
  }, [state]);

  return (
    <ThemedView style={styles.root}>
      <SafeAreaView style={styles.safe} edges={['top', 'left', 'right']}>
        <View style={styles.header}>
          <Pressable
            onPress={() => router.replace('/belanja' as never)}
            style={({ pressed }) => [styles.backBtn, pressed && styles.pressed]}
            accessibilityRole="button"
            accessibilityLabel="Kembali"
          >
            <ThemedText style={styles.backArrow}>←</ThemedText>
          </Pressable>
          <ThemedText type="subtitle" style={styles.headerTitle}>Riwayat Transaksi</ThemedText>
          <View style={styles.headerSpacer} />
        </View>

        {state.kind === 'loading' && (
          <View style={styles.centerBox}>
            <ActivityIndicator color={Brand.primary} />
            <ThemedText themeColor="textSecondary" style={styles.centerText}>
              Memuat transaksi…
            </ThemedText>
          </View>
        )}

        {state.kind === 'error' && (
          <View style={styles.centerBox}>
            <ThemedText style={styles.errorText}>{state.message}</ThemedText>
          </View>
        )}

        {state.kind === 'ready' && (
          <FlatList
            data={state.items}
            keyExtractor={(item) => `${item.row_type}-${item.id}`}
            renderItem={({ item }) => <SaldoHistoryRow item={item} />}
            contentContainerStyle={styles.listContent}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
            onEndReached={onEndReached}
            onEndReachedThreshold={0.5}
            ListEmptyComponent={(
              <View style={styles.centerBox}>
                <ThemedText themeColor="textSecondary" style={styles.centerText}>
                  Belum ada transaksi. Yuk mulai top up point pertama!
                </ThemedText>
              </View>
            )}
            ListFooterComponent={
              loadingMore ? (
                <ActivityIndicator color={Brand.primary} style={styles.footerSpinner} />
              ) : !state.hasMore && state.items.length > 0 ? (
                <ThemedText themeColor="textSecondary" style={styles.footerText}>
                  Semua transaksi sudah ditampilkan.
                </ThemedText>
              ) : null
            }
          />
        )}
      </SafeAreaView>
    </ThemedView>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#fcfbf8' },
  safe: { flex: 1 },
  header: {
    alignItems: 'center',
    borderBottomColor: '#e5e5e5',
    borderBottomWidth: 1,
    flexDirection: 'row',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
  },
  backBtn: { alignItems: 'center', height: 40, justifyContent: 'center', width: 40 },
  backArrow: { color: Brand.primary, fontSize: 24, fontWeight: '600' },
  headerTitle: { color: Colors.light.text, flex: 1, fontSize: 18, fontWeight: '700', textAlign: 'center' },
  headerSpacer: { width: 40 },
  listContent: {
    alignSelf: 'center',
    maxWidth: MaxContentWidth,
    padding: Spacing.four,
    paddingBottom: Spacing.six,
    width: '100%',
  },
  separator: { height: Spacing.two },
  centerBox: { alignItems: 'center', gap: Spacing.two, paddingVertical: Spacing.six },
  centerText: { fontSize: 13, textAlign: 'center' },
  errorText: { color: '#b3261e', fontSize: 14, lineHeight: 20, textAlign: 'center' },
  footerSpinner: { marginTop: Spacing.three },
  footerText: { fontSize: 12, marginTop: Spacing.three, textAlign: 'center' },
  pressed: { opacity: 0.75 },
});
